/**
 * Purchasing Component - Handles the shopping and checkout functionality
 * 
 * Features:
 * - Displays items from price list and canteen
 * - Allows adding/removing items to cart
 * - Handles checkout and payment processing
 * - Supports multiple payment methods
 * - Integrates with fiscalization system
 */
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { hasPermission } from "../../context/AuthFunc";
import { PERMISSIONS } from "../../constants/const";
import {
  TextField,
  FormControlLabel,
  RadioGroup,
  Button,
  Container,
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Pagination, Dialog, DialogTitle, Switch, DialogContent, ListItemIcon, Checkbox, DialogActions, Radio, Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Error from '../Error/Error';
import {AccountBalanceWallet, Add, CreditCard, FlashOn, Payment, Receipt, Redeem, Remove} from "@mui/icons-material";
import {
  closePrinterSession,
  fiskalizacija,
  getArticles,
  getFiscalizationMode, getFiscalizationStatus,
  getPriceList,
  openPrinterSession,
  sendTicketData
} from "../../api/axios";

// Default number of items per page
const ITEMS_PER_PAGE = 20;

/**
 * Main Shopping Component
 */
const Shopping = () => {
  // Translation hook for multilingual support
  const { t } = useTranslation("global");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State management
  const [items, setItems] = useState([]); // All available items
  const [cart, setCart] = useState([]); // Items in shopping cart
  const [currentPage, setCurrentPage] = useState(1); // Current pagination page
  const [itemsPerPage, setItemsPerPage] = useState(20); // Items per page
  const [openPopup, setOpenPopup] = useState(false); // Controls popup visibility
  const [selectedPayment, setSelectedPayment] = useState(""); // Selected payment method
  const [isTestMode, setIsTestMode] = useState(false); // Fiscalization test mode flag
  const [localPayment, setLocalPayment] = useState(""); // Local payment method
  const [ticketFilter, setTicketFilter] = useState("all"); // Filter for item types
  const [printError, setPrintError] = useState(null); // Printing error state
  const [fiscalizationError, setFiscalizationError] = useState(null); // Fiscalization error state
  const [showPaymentPopup, setShowPaymentPopup] = useState(false); // Payment popup visibility
  const [showPlasticCardPopup, setShowPlasticCardPopup] = useState(false);
  const [isAdvance, setIsAdvance] = useState(false);
  const [isFiscalizationEnabled, setIsFiscalizationEnabled] = useState(true);

  /**
   * Handles pagination page change
   * @param {Object} event - The event object
   * @param {number} newPage - The new page number
   */
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  /**
   * Filters items based on current filter selection
   * @returns {Array} Filtered items array
   */
  const getFilteredItems = () => {
    const filtered = items.filter(item => {
      if (ticketFilter === "all") return true;
      if (ticketFilter === "paper") return item.kind === "PAPIRNA";
      if (ticketFilter === "plastic") return item.kind === "PLASTICNA";
      if (ticketFilter === "canteen") return item.name && !item.kind;
      if (ticketFilter === "other") return !item.kind && !item.name;
      return true;
    });
    return filtered;
  };

  const fetchPriceList = async () => {
      try {
        const response = await getPriceList();
        const canteenResponse = await getArticles();
        if (response && Array.isArray(response)) {
          setItems(response);
        }
        else {
          console.error("Unexpected API response:", response);
          setItems([]);
        }
        if (canteenResponse && Array.isArray(canteenResponse)){
          setItems((prev) =>
            [...prev, ...canteenResponse]
          )
        }
        else {
          console.error("Unexpected API response:", canteenResponse);
          setItems([]);
        }
        const modeResponse = await getFiscalizationMode();
        setIsTestMode(modeResponse === "1");

      } catch (error) {
        console.error("Failed to fetch initial data:", error);
        setIsTestMode(false);
        console.error("Failed to fetch price list:", error);
        setItems([]);
      }
      console.log(items);
    };
  /**
   * Gets paginated subset of filtered items
   * @returns {Array} Paginated items array
   */
  const getPaginatedItems = () => {
    const filteredItems = getFilteredItems();
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    console.log(startIndex + " " + endIndex)
    return filteredItems.slice(startIndex, endIndex);
  };

  // Reset to first page when filters or items change
  useEffect(()=>{
    setCurrentPage(1);
  },[ticketFilter, items])

  // Fetch initial data on component mount
  useEffect(() => {
    /**
     * Fetches price list and articles from API
     */
    
    fetchPriceList();
    fetchFiscalizationEnabled();

  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchFiscalizationEnabled(); // ponovno učitavanje statusa
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  /**
   * Adds item to cart or increments quantity if already in cart
   * @param {Object} item - The item to add
   */
  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
            cartItem.id === item.id
                ? { ...cartItem, quantity: cartItem.quantity + 1 }
                : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
  };

  /**
   * Removes item from cart or decrements quantity
   * @param {string} itemId - ID of item to remove
   */
  const removeFromCart = (itemId) => {
    setCart((prevCart) =>
        prevCart
            .map((cartItem) =>
                cartItem.id === itemId
                    ? { ...cartItem, quantity: cartItem.quantity - 1 }
                    : cartItem
            )
            .filter((cartItem) => cartItem.quantity > 0)
    );
  };

  /**
   * Calculates total price of items in cart
   * @returns {string} Formatted total price
   */
  const calculateTotal = () => {
    return cart
        .reduce((total, item) => total + item.quantity * (item.price || item.unit_price), 0)
        .toFixed(2);
  };

  /**
   * Handles the printing and fiscalization process
   * @param {Array} selectedPayments - Array of payment methods
   */
  const handlePrint = async (selectedPayments, isAdvance) => {
    if (cart.length === 0) {
      return;
    }

    try {
      setPrintError(null);
      setFiscalizationError(null);

      console.log("🔵 Otvaranje sesije štampanja...");
      await openPrinterSession();

      console.log("🟢 Slanje podataka za svaku kartu...");
      for (const item of cart) {
        console.log(item);

        if (item.type.split(" ")[1] === "KARTICA" || item.type.split(" ")[1] === "KARTA") {
          if(item.quantity > 1){
            for (let i = 0; i < item.quantity; i++) {
              const ticketData = {
                id: item.id,
                category: item.category,
                kind: item.kind,
                startDate: new Date(),
                endDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
                price: item.price || item.unit_price,
                discount: item.discount,
                description: item.description,
                type: item.name || item.type,
                vat: item.vat,
              };
              await sendTicketData(ticketData, selectedPayments, isTestMode, isAdvance);
            }
          }else {
            const ticketData = {
              id: item.id,
              category: item.category,
              kind: item.kind,
              startDate: new Date(),
              endDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
              price: item.price || item.unit_price,
              discount: item.discount,
              description: item.description,
              type: item.name || item.type,
              vat: item.vat,
            };
            await sendTicketData(ticketData, selectedPayments, isTestMode, isAdvance);
          }
        }
      }
      if (isFiscalizationEnabled) {
        await fiskalizacija(cart, selectedPayments, isTestMode, isAdvance);
      }

      console.log("🟣 Generisanje PDF-a i štampa...");

      setCart([]);
      setPrintError(null);
      setFiscalizationError(null);

    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 400) {
        setFiscalizationError(`Greška pri fiskalizaciji (400 Bad Request): ${error.response.data?.message || "Nepoznata greška."}`);
      } else {
        setPrintError("Greška prilikom štampe računa! Molimo pokušajte ponovo.");
      }
    }
  };

  const fetchFiscalizationEnabled = async () => {
    try {
      const response = await getFiscalizationStatus(); // Pretpostavlja se da vraća "1" ili "0"
      setIsFiscalizationEnabled(response === "1");
    } catch (error) {
      console.error("Greška prilikom dohvatanja statusa fiskalizacije:", error);
      setIsFiscalizationEnabled(false); // podrazumevano false ako padne API
    }
  };

  /**
   * PaymentPopup Component - Handles payment method selection
   * @param {boolean} open - Controls popup visibility
   * @param {function} onClose - Close handler
   * @param {function} onConfirm - Confirmation handler
   * @param {number} totalAmount - Total amount to pay
   */
    const PlasticCardPopup = () => {
      const [errMsg, setErrMsg] = useState('');
      const [currentItemIndex, setCurrentItemIndex] = useState(0);
      const [serialNumbers, setSerialNumbers] = useState({});
      const [showConfirmation, setShowConfirmation] = useState(false);
      const [isEditing, setIsEditing] = useState(false);
      const [editIndex, setEditIndex] = useState(null);
      const [originalPosition, setOriginalPosition] = useState(0);

      const inputRef = useRef(null);

      useEffect(() => {
        const popupOpen = showPlasticCardPopup && !showConfirmation;

        if (showPlasticCardPopup && !showConfirmation) {
          const timeout = setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 0);
          return () => clearTimeout(timeout);
        }
      }, [currentItemIndex, showPlasticCardPopup, currentItemIndex]);
    
    function normalizePlasticnaItems(items) {
      return items
        .filter(item => item.kind === "PLASTICNA")
        .flatMap(item =>
          Array.from({ length: item.quantity }, () => ({
            ...item,
            quantity: 1,
            serialNumber: ''
          }))
        );
    }

    const plasticCardItems = normalizePlasticnaItems(cart);
    console.log(plasticCardItems);
    const handleSerialNumberChange = (e) => {
      setSerialNumbers(prev => ({
        ...prev,
        [currentItemIndex]: e.target.value
      }));
    };

    const handleNext = () => {
      const currentSerial = serialNumbers[currentItemIndex]?.trim();

      if (!currentSerial) return;

      const isDuplicate = Object.values(serialNumbers).some(
        (sn, idx) => idx !== currentItemIndex && sn.trim() === currentSerial
      );

      if (isDuplicate) {
        setErrMsg("Ova kartica je već uneta. Unesite jedinstveni serijski broj.");
        setSerialNumbers(prev => ({
          ...prev,
          [currentItemIndex]: ""
        }));
        return;
      }

      if (isEditing) {
        // Return to original position after edit
        setCurrentItemIndex(originalPosition);
        setIsEditing(false);
      } else if (currentItemIndex < plasticCardItems.length - 1) {
        setCurrentItemIndex(prev => prev + 1);
      } else {
        setShowConfirmation(true);
      }
    };

    const handleConfirm = () => {
      // Update cart with all serial numbers
      const updatedItems = plasticCardItems.map((item, index) => ({
        ...item,
        serialNumber: serialNumbers[index] || ''
      }));

      plasticCardItems.forEach(element => {
        
      });

      setShowPlasticCardPopup(false);
      setShowPaymentPopup(true);
    };

    const handleEdit = (index) => {
      setCurrentItemIndex(index);
      setShowConfirmation(false);
      setIsEditing(true);
    };

    if (showConfirmation) {
      return (
        <Dialog open={true} onClose={() => setShowPlasticCardPopup(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Potvrda unetih serijskih brojeva</DialogTitle>
          <DialogContent>
            <List>
              {plasticCardItems.map((item, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={`${item.name || item.type} (Kartica ${index + 1})`}
                    secondary={`Serijski broj: ${serialNumbers[index] || 'Nije unet'}`}
                  />
                  <Button 
                    onClick={() => {
                      setEditIndex(index);
                      setCurrentItemIndex(index);
                      setShowConfirmation(false);
                    }}
                    variant="outlined"
                    size="small"
                  >
                    Izmeni
                  </Button>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPlasticCardPopup(false)}>Otkaži</Button>
            <Button onClick={handleConfirm} variant="contained" color="primary">
              Potvrdi i nastavi na plaćanje
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

      return (
        <Dialog open={true} onClose={() => {
          if (isEditing) {
            setIsEditing(false);
            setShowConfirmation(true);
          } else {
            setShowPlasticCardPopup(false);
          }
        }}>
          <DialogTitle>
            Unesite serijski broj za karticu {currentItemIndex + 1}/{plasticCardItems.length}
          </DialogTitle>
          <DialogContent>
            <TextField
              inputRef={inputRef}
              margin="dense"
              label="Serijski broj"
              fullWidth
              value={serialNumbers[currentItemIndex] || ''}
              onChange={handleSerialNumberChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleNext();
                }
              }}
              placeholder='Prislonite karticu'
            />
            {errMsg && <Error message={errMsg} onClose={() => setErrMsg('')} />}

            {/* Show previously entered cards */}
            {Object.keys(serialNumbers).length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle1">Unete kartice:</Typography>
                <List dense>
                  {Object.entries(serialNumbers)
                    .filter(([idx]) => idx != currentItemIndex && serialNumbers[idx])
                    .map(([idx, sn]) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={`${plasticCardItems[idx].name || plasticCardItems[idx].type} (Kartica ${parseInt(idx)+1})`}
                          secondary={`Serijski broj: ${sn}`}
                        />
                        <Button
                          onClick={() => {
                            setOriginalPosition(currentItemIndex);
                            setIsEditing(true);
                            setCurrentItemIndex(parseInt(idx));
                            if (inputRef.current) {
                              inputRef.current.focus();
                            }
                          }}
                          variant="outlined"
                          size="small"
                        >
                          Izmeni
                        </Button>
                      </ListItem>
                    ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              if (isEditing) {
                setIsEditing(false);
                setShowConfirmation(true);
              } else {
                setShowPlasticCardPopup(false);
              }
            }}>Otkaži</Button>
            <Button 
              onClick={handleNext} 
              variant="contained"
              disabled={!serialNumbers[currentItemIndex]}
            >
              {currentItemIndex < plasticCardItems.length - 1 ? 'Sledeća' : 'Pregled'}
            </Button>
          </DialogActions>
        </Dialog>
      );
  }
    

  const PaymentPopup = ({ open, onClose, onConfirm, totalAmount = 0 }) => {
    const [payments, setPayments] = useState([{ method: "", fullAmount: true, amount: totalAmount }]);


    const paymentOptions = [
      { label: "Gotovina", value: "Cash", icon: <Payment /> },
      { label: "Kartica", value: "Card", icon: <CreditCard /> },
      { label: "Ček", value: "Check", icon: <Receipt /> },
      { label: "Vaučer", value: "Voucher", icon: <Redeem /> },
      { label: "Instant", value: "Instant", icon: <FlashOn /> },
      { label: "Drugo", value: "OtherNonCash", icon: <AccountBalanceWallet /> },
    ];

    const remainingAmount = () => {
      const entered = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const remaining = totalAmount - entered;
      return remaining < 0 ? 0 : remaining;
    };

    const totalEntered = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const canAddMore = remainingAmount() > 0 && payments.length < paymentOptions.length;

    const remainingAmountForIndex = (currentIndex) => {
      const enteredByOthers = payments.reduce((sum, p, idx) => {
        if (idx === currentIndex) return sum;
        return sum + parseFloat(p.amount || 0);
      }, 0);
      const remaining = totalAmount - enteredByOthers;
      return remaining < 0 ? 0 : remaining;
    };

    const handleMethodChange = (index, value) => {
      const updated = [...payments];
      updated[index].method = value;
      updated[index].fullAmount = true;
      updated[index].amount = remainingAmountForIndex(index);
      setPayments(updated);
    };

    const handleFullAmountChange = (index, isFull) => {
      const updated = [...payments];
      updated[index].fullAmount = isFull;
      updated[index].amount = isFull ? remainingAmountForIndex(index) : "";
      setPayments(updated);
    };

    const handleAmountChange = (index, value) => {
      const updated = [...payments];
      updated[index].amount = value;
      setPayments(updated);
    };

    const addAnotherMethod = () => {
      if (canAddMore) {
        setPayments([...payments, { method: "", fullAmount: true, amount: remainingAmount() }]);
      }
    };

    const isFormValid = () => {
      return payments.every(p =>
          p.method !== "" &&
          p.amount !== "" &&
          !isNaN(parseFloat(p.amount)) &&
          parseFloat(p.amount) > 0
      ) && totalEntered === totalAmount;
    };

    const handleConfirm = () => {
      onConfirm(payments, isAdvance);
      onClose();
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
          <DialogContent dividers>
            {payments.map((payment, index) => (
                <Box key={index} mb={3}>
                  <Typography variant="body2" mb={1}>
                    Način plaćanja #{index + 1}
                  </Typography>
                  <List>
                    {paymentOptions.map((option) => (
                        <ListItem key={option.value} dense>
                          <ListItemIcon>{option.icon}</ListItemIcon>
                          <ListItemText primary={option.label} />
                          <Radio
                              edge="end"
                              checked={payment.method === option.value}
                              onChange={() => handleMethodChange(index, option.value)}
                          />
                        </ListItem>
                    ))}
                  </List>

                  {payment.method && (
                      <Box mt={1} ml={1}>
                        <Typography variant="body2" mb={1}>
                          Iznos plaćanja:
                        </Typography>
                        <RadioGroup
                            value={payment.fullAmount ? "full" : "partial"}
                            onChange={(e) => handleFullAmountChange(index, e.target.value === "full")}
                        >
                          <FormControlLabel
                              value="full"
                              control={<Radio />}
                              label={`Ceo iznos (${remainingAmountForIndex(index).toFixed(2)} RSD)`}
                          />
                          <FormControlLabel value="partial" control={<Radio />} label="Unesi iznos" />
                        </RadioGroup>

                        {!payment.fullAmount && (
                            <TextField
                                fullWidth
                                type="number"
                                label="Iznos"
                                value={payment.amount}
                                onChange={(e) => handleAmountChange(index, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  max: remainingAmountForIndex(index),
                                  step: "0.01"
                                }}
                            />
                        )}
                      </Box>
                  )}
                </Box>
            ))}

            <Box display="flex" justifyContent="space-between" mt={2} mb={1}>
              <Typography>Ukupno: {(totalAmount || 0).toFixed(2)} RSD</Typography>
              <Typography>Uneto: {totalEntered.toFixed(2)} RSD</Typography>
              <Typography>Ostatak: {remainingAmount().toFixed(2)} RSD</Typography>
            </Box>

            {canAddMore && (
                <Box mt={2} textAlign="center">
                  <Button variant="outlined" onClick={addAnotherMethod} startIcon={<Add />}>
                    Dodaj još jedan način plaćanja
                  </Button>
                </Box>
            )}
          </DialogContent>
          <FormControlLabel
              control={
                <Checkbox
                    checked={isAdvance}
                    onChange={(e) => setIsAdvance(e.target.checked)}
                />
              }
              label="Avansno plaćanje"
          />

          <DialogActions>
            <Button onClick={handleConfirm} variant="contained" disabled={!isFormValid()}>
              ✓ Potvrdi
            </Button>
            <Button onClick={onClose} variant="outlined" color="error">
              ✗ Poništi
            </Button>
          </DialogActions>
        </Dialog>
    );
  };

  /**
   * Handles checkout process
   */
  const handleCheckout = () => {
    const plasticCardItems = cart
        .filter(item => item.kind === "PLASTICNA")
        .flatMap(item =>
          Array.from({ length: item.quantity }, () => ({
            ...item,
            quantity: 1,
            serialNumber: ''
          }))
        );
    if (!isFiscalizationEnabled) {
      handlePrint([], false);
      return;
    }
    if(plasticCardItems.length > 0){
      setShowPlasticCardPopup(true);
    }else{
      setShowPaymentPopup(true);
    }
  };

  const handlePlasticCardPopupClose = () => {
    setShowPaymentPopup(true);
  }

  /**
   * Handles payment popup close
   */
  const handlePaymentPopupClose = () => {
    setShowPaymentPopup(false);
  };


  return (
    <Container maxWidth={false} sx={{ padding: "6px"}}>
      {!isMobile ? (
        <Grid container spacing={2} sx={{flexWrap: "wrap"}} >
          <Grid item xs={12} sm={8}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={1}>
            <RadioGroup 
              row
              name="ticket-filter"
              value={ticketFilter}
              onChange={(e) => setTicketFilter(e.target.value)}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={t("shopping.all")}
                sx={{ marginRight: 0 }}
              />
              <FormControlLabel
                value="paper"
                control={<Radio size="small" sx={{ color: "#f8af46", '&.Mui-checked': { color: "#f8af46" } }} />}
                label={t("shopping.paper")}
                sx={{ marginRight: 0 }}
              />
              <FormControlLabel
                value="plastic"
                control={<Radio size="small" sx={{ color: "#ec7732", '&.Mui-checked': { color: "#ec7732" } }} />}
                label={t("shopping.plastic")}
                sx={{ marginRight: 0 }}
              />
              {(hasPermission(PERMISSIONS.NEW_EMPLOYEE) || 
               hasPermission(PERMISSIONS.ORGANIZATION_UNITS) || 
               hasPermission(PERMISSIONS.ARTICLE) || 
               hasPermission(PERMISSIONS.UNIT_OF_MEASURE) || 
               hasPermission(PERMISSIONS.INGREDIENTS)) && (
                <FormControlLabel
                  value="canteen"
                  control={<Radio size="small" sx={{ color: "#9c27b0", '&.Mui-checked': { color: "#9c27b0" } }} />}
                  label={t("shopping.canteen")}
                  sx={{ marginRight: 0 }}
                />
              )}
              <FormControlLabel
                value="other"
                control={<Radio size="small" sx={{ color: "#6ec8af", '&.Mui-checked': { color: "#6ec8af" } }} />}
                label={t("shopping.other")}
                sx={{ marginRight: 0 }}
              />
              
            </RadioGroup>
              <Pagination
                  count={Math.ceil(getFilteredItems().length / itemsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
              />
            </Box>
            <Grid container spacing={1} mt={1} sx={{height: "80vh", alignContent: "flex-start"}}>
              {getPaginatedItems().length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {t("shopping.noItems")}
                    </Typography>
                  </Grid>
              ) : (
                  getPaginatedItems().map((item) => (
                      <Grid
                          item
                          xs={12}
                          sm={3}
                          onClick={() => addToCart(item)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor:
                                item.kind === "PAPIRNA"
                                    ? "#f8af46"
                                    : item.kind === "PLASTICNA"
                                        ? "#ec7732"
                                        : item.name
                                            ? "#9c27b0"
                                            : "#6ec8af",
                            "&:hover": {
                              backgroundColor:
                                  item.kind === "PAPIRNA"
                                      ? "#f4a03c"
                                      : item.kind === "PLASTICNA"
                                          ? "#e5631f"
                                          : item.name
                                              ? "#8e24aa"
                                              : "#5eb59c",
                              borderRadius: "8px",
                            },
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: "8px",
                            color: "black",
                            border: "1px solid gray",
                            height: "20%"
                          }}
                      >
                        <div style={{padding: "8px"}}>
                          <Typography variant="body1" fontWeight="bold" sx={{fontSize: "1.1vmax"}}>
                            {item.name || item.type}
                          </Typography>
                          <Typography variant="body2" sx={{fontSize: "1vmax"}}>
                            {`${t("shopping.category")}: ${item.category || "-"}`}
                          </Typography>
                          <Typography variant="body2" sx={{fontSize: "1vmax"}}>
                            {`${t("shopping.price")}: ${item.price || item.unit_price} RSD`}
                          </Typography>
                        </div>
                      </Grid>
                  ))
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} sm={4} md={4} lg={4} xl={4} sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "8px",
          }}>
            <Box>
              <Typography variant="h6">{t("shopping.cart")}</Typography>
              <List sx={{
                height: "65vh",
                overflow: "auto",
              }}>
                {cart.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText
                          primary={item.name || item.type}
                          secondary={
                            <>
                              {`${t("shopping.price")}: ${item.price || item.unit_price} RSD`}
                              <br />
                            </>
                          }
                      />
                      <Box display="flex" alignItems="center">
                        <IconButton
                            size="small"
                            onClick={() => removeFromCart(item.id)}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="body1" mx={1}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => addToCart(item)}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </ListItem>
                ))}
              </List>
            </Box>
            <Box mt={2}>
              <Typography variant="h6">
                {t("shopping.total")}: {calculateTotal()} RSD
              </Typography>
              <Button
                  variant="contained"
                  color="primary"
                  disabled={cart.length === 0}
                  fullWidth
                  onClick={handleCheckout}
              >
                {t("shopping.checkout")}
              </Button>
              {showPlasticCardPopup && (<PlasticCardPopup/>)}
                
              <PaymentPopup
                  open={showPaymentPopup}
                  onClose={handlePaymentPopupClose}
                  totalAmount={parseFloat(calculateTotal())}
                  onConfirm={(selectedPayments) => {
                    console.log("Izabrani načini plaćanja:", selectedPayments);
                    handlePrint(selectedPayments, isAdvance)
                  }}
              />

              {fiscalizationError && (
                  <Box mt={2}>
                    <Alert severity="error" onClose={() => setFiscalizationError(null)}>
                      {fiscalizationError}
                    </Alert>
                  </Box>
              )}

              {printError && (
                  <Box mt={2}>
                    <Alert severity="error" onClose={() => setPrintError(null)}>
                      {printError}
                    </Alert>
                  </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Items List - Mobile */}
          <Box>
            <Box display="flex" flexDirection={"column"} mb={2}>
              <RadioGroup 
                row
                name="ticket-filter"
                value={ticketFilter}
                onChange={(e) => setTicketFilter(e.target.value)}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label={t("shopping.all")}
                  sx={{ marginRight: 0 }}
                />
                <FormControlLabel
                  value="paper"
                  control={<Radio size="small" sx={{ color: "#f8af46", '&.Mui-checked': { color: "#f8af46" } }} />}
                  label={t("shopping.paper")}
                  sx={{ marginRight: 0 }}
                />
                <FormControlLabel
                  value="plastic"
                  control={<Radio size="small" sx={{ color: "#ec7732", '&.Mui-checked': { color: "#ec7732" } }} />}
                  label={t("shopping.plastic")}
                  sx={{ marginRight: 0 }}
                />
                {(hasPermission(PERMISSIONS.NEW_EMPLOYEE) || 
                 hasPermission(PERMISSIONS.ORGANIZATION_UNITS) || 
                 hasPermission(PERMISSIONS.ARTICLE) || 
                 hasPermission(PERMISSIONS.UNIT_OF_MEASURE) || 
                 hasPermission(PERMISSIONS.INGREDIENTS)) && (
                  <FormControlLabel
                    value="canteen"
                    control={<Radio size="small" sx={{ color: "#9c27b0", '&.Mui-checked': { color: "#9c27b0" } }} />}
                    label={t("shopping.canteen")}
                    sx={{ marginRight: 0 }}
                  />
                )}
                <FormControlLabel
                  value="other"
                  control={<Radio size="small" sx={{ color: "#6ec8af", '&.Mui-checked': { color: "#6ec8af" } }} />}
                  label={t("shopping.other")}
                  sx={{ marginRight: 0 }}
                />
              </RadioGroup>
              <Pagination
                count={Math.ceil(getFilteredItems().length / itemsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>

            <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
              {getPaginatedItems().length === 0 ? (
                <Typography variant="body1" sx={{ p: 2 }}>
                  {t("shopping.noItems")}
                </Typography>
              ) : (
                getPaginatedItems().map((item) => (
                  <Box 
                    onClick={() => addToCart(item)}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: item.kind === "PAPIRNA" ? "#f8af46" :
                               item.kind === "PLASTICNA" ? "#ec7732" :
                               item.name ? "#9c27b0" : "#6ec8af",
                      color: 'black',
                      cursor: 'pointer'
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {item.name || item.type}
                    </Typography>
                    <Typography variant="body2">
                      {`${t("shopping.category")}: ${item.category || "-"}`}
                    </Typography>
                    <Typography variant="body2">
                      {`${t("shopping.price")}: ${item.price || item.unit_price} RSD`}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          {/* Cart - Mobile */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
            <Typography variant="h6">{t("shopping.cart")}</Typography>
            <Box sx={{ maxHeight: '30vh', overflow: 'auto', mb: 2 }}>
            
                {cart.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box>
                      <Typography>{item.name || item.type}</Typography>
                      <Typography variant="body2">
                        {`${t("shopping.price")}: ${item.price || item.unit_price} RSD`}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => removeFromCart(item.id)}>
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography variant="body1" mx={1}>
                          {item.quantity}
                        </Typography>
                      <IconButton size="small" onClick={() => addToCart(item)}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              }
            </Box>

            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("shopping.total")}: {calculateTotal()} RSD
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={cart.length === 0}
              fullWidth
              onClick={handleCheckout}
            >
              {t("shopping.checkout")}
            </Button>
            {showPlasticCardPopup && (<PlasticCardPopup/>)}
                
              <PaymentPopup
                  open={showPaymentPopup}
                  onClose={handlePaymentPopupClose}
                  totalAmount={parseFloat(calculateTotal())}
                  onConfirm={(selectedPayments) => {
                    console.log("Izabrani načini plaćanja:", selectedPayments);
                    handlePrint(selectedPayments)
                  }}
              />

              {fiscalizationError && (
                  <Box mt={2}>
                    <Alert severity="error" onClose={() => setFiscalizationError(null)}>
                      {fiscalizationError}
                    </Alert>
                  </Box>
              )}

              {printError && (
                  <Box mt={2}>
                    <Alert severity="error" onClose={() => setPrintError(null)}>
                      {printError}
                    </Alert>
                  </Box>
              )}
          </Box>
        </Box>
      )}
    </Container>
    );
  };

  export default Shopping;
